import { groupBy, values } from 'lodash'
import {
  FinancialAccount,
  FinancialType,
} from '@/reducers/finances/financialAccountsReducer'
import {
  SyncErrorClassification,
  type PlaidItemSyncStatus,
} from '@/actions/plaid/authActions'

interface ItemErrorSignal {
  /** Reads a connection field the sync pipeline does not write. */
  matches: (item: PlaidItemSyncStatus) => boolean
  classification: SyncErrorClassification
}

/**
 * Connection problems recorded on the Plaid item rather than by the sync pipeline.
 * A disconnection webhook sets these and runs no sync, so no classification exists.
 */
const ITEM_ERROR_SIGNALS: ItemErrorSignal[] = [
  {
    matches: (item) => item.needsReconnection,
    classification: SyncErrorClassification.REAUTH_REQUIRED,
  },
]

/**
 * A sync-status item carrying the error state this surface renders.
 *
 * The payload holds the sync pipeline's classification and the item's own
 * connection fields. `resolvedErrorClassification` is the single answer derived
 * from both, and it is the one the surfaces read — see
 * {@link resolveSyncErrorClassification}.
 */
export type ResolvedPlaidItemSyncStatus = PlaidItemSyncStatus & {
  resolvedErrorClassification: SyncErrorClassification | null
}

/**
 * The error state to render for one item. The sync pipeline's classification is the
 * most specific answer, so it wins; otherwise the item's own fields decide.
 */
export const resolveSyncErrorClassification = (
  syncItem: PlaidItemSyncStatus
): SyncErrorClassification | null => {
  if (syncItem.syncErrorClassification) return syncItem.syncErrorClassification

  const signal = ITEM_ERROR_SIGNALS.find(({ matches }) => matches(syncItem))

  return signal?.classification ?? null
}

/**
 * Off while manual accounts are kept out of the self-serve surface — the list,
 * the "Add account manually" entry point, and the filter options that derive
 * from the list. The components stay in place, so the whole capability comes
 * back by flipping this to `true`.
 */
export const SHOW_MANUAL_ACCOUNTS = false

/**
 * Both flavours of manual: `MANUAL` is one a member added themselves when their
 * bank isn't supported; `USER_MANUAL_UPLOAD` is the bucket Heard creates to hold
 * manually-uploaded transactions, which the member never created and cannot act on.
 */
export const isManualAccount = (account: FinancialAccount) =>
  account.accountType === FinancialType.MANUAL ||
  account.accountType === FinancialType.USER_MANUAL_UPLOAD

/**
 * Off while members cannot deactivate or reactivate their own accounts —
 * deactivation cancels outstanding statement requests and drops the account out
 * of reconciliation, leaving those months unrecoverable, and reactivation is
 * withdrawn alongside it so the pair stays coherent. Both are the only entries
 * in the account ⋮ menu, so the menu itself stops rendering; the options,
 * handlers and dialogs stay in place behind this flag.
 */
export const SHOW_ACCOUNT_ACTIONS = false

/**
 * Whether an account belongs on the self-serve surface at all.
 *
 * Inactive rides with the actions rather than having its own switch: with no way
 * to reactivate one, listing it only shows the member something they cannot act on.
 */
export const isVisibleAccount = (account: FinancialAccount) => {
  const manualAllowed = SHOW_MANUAL_ACCOUNTS || !isManualAccount(account)
  const inactiveAllowed = SHOW_ACCOUNT_ACTIONS || !account.inactive
  const isShown = !account.isHidden
  const hasLiveConnection = !account.accounts?.inactive

  return manualAllowed && inactiveAllowed && isShown && hasLiveConnection
}

export interface SortedAccounts {
  businessAccountsList: FinancialAccount[]
  personalAccountsList: FinancialAccount[]
}

/**
 * Partitions accounts into business vs. personal/inactive, and within each group orders them by
 * institution then balance:
 *
 * - accounts are grouped by institution (all of one bank are contiguous),
 * - within a group, highest balance first,
 * - groups are ordered by their top account's balance (the bank holding the single
 *   largest-balance account leads).
 *
 * Errored accounts don't render a balance, so they can't be balance-ranked — they're treated as
 * balance-less and sink to the bottom of their group (a fully-errored bank sinks last).
 */
export const sortAccounts = (
  accounts: FinancialAccount[],
  erroredPlaidItemIds: ReadonlyMap<number, unknown>
): SortedAccounts => {
  const sortBalance = (account: FinancialAccount): number | null =>
    account.plaidItemId !== null && erroredPlaidItemIds.has(account.plaidItemId)
      ? null
      : account.currentBalanceInCents

  /** Higher balance first; accounts with no sortable balance go last. */
  const compareByBalanceDesc = (
    a: FinancialAccount,
    b: FinancialAccount
  ): number => {
    const balanceA = sortBalance(a)
    const balanceB = sortBalance(b)
    if (balanceA === null && balanceB === null) return 0
    if (balanceA === null) return 1
    if (balanceB === null) return -1
    return balanceB - balanceA
  }

  const groupByInstitutionSortedByBalance = (
    list: FinancialAccount[]
  ): FinancialAccount[] =>
    values(groupBy(list, (account) => account.plaidInstitutionName ?? ''))
      .map((group) => [...group].sort(compareByBalanceDesc))
      .sort((groupA, groupB) => compareByBalanceDesc(groupA[0], groupB[0]))
      .flat()

  const businessAccountsList: FinancialAccount[] = []
  const personalAccountsList: FinancialAccount[] = []

  for (const account of accounts) {
    if (account.markedPersonalAt !== null || account.inactive) {
      personalAccountsList.push(account)
    } else {
      businessAccountsList.push(account)
    }
  }

  return {
    businessAccountsList:
      groupByInstitutionSortedByBalance(businessAccountsList),
    personalAccountsList:
      groupByInstitutionSortedByBalance(personalAccountsList),
  }
}
